import 'react-block-ui/style.css';

import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton, Stack, TextField } from '@fluentui/react';

import { AddressInput, SuggestAddress } from '../core/AddressInput';
import { ProviderAddress } from '../../api/providers.types';
import { getFullProviderAddressString } from '../../util/Address';

export interface ProviderAddressDialogProps {
  providerId: string;
  address?: ProviderAddress;
  onEditAddress: (addressRequest: Partial<ProviderAddress>) => void;
}

export interface ProviderAddressDialogState {
  editAddress: ProviderAddress;
  suggest?: SuggestAddress;
  unit?: string;
  lastSelectedAddress?: string;
  currentAddressTextValue?: string;
  initialUnitValue?: string;
  initialAddressValue?: string;
}

export class ProviderAddressDialog {
  public static show(options: ProviderAddressDialogProps) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(<ProviderAddressDialogInternal {...options} close={close} />, dialogNode);
  }
}

class ProviderAddressDialogInternal extends React.Component<ProviderAddressDialogProps & { close: () => void }, ProviderAddressDialogState> {
  constructor(props: any) {
    super(props);

    const addressString = getFullProviderAddressString(props.address);
      
    this.state = {
      editAddress: props.address || {},
      unit: props.address?.addressLine2 || '',
      lastSelectedAddress: addressString || '',
      currentAddressTextValue: addressString || '',
      initialUnitValue: props.address?.addressLine2 || '',
      initialAddressValue: addressString || '',
    };
  }

  public render(): JSX.Element {
    return (
      <Dialog
        hidden={false}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Edit Provider Home Address',
        }}
        onDismiss={this.close}
        modalProps={{
          isBlocking: true,
          className: "dialog-allow-overflow",
        }}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <AddressInput initialValue={getFullProviderAddressString(this.state.editAddress)} onSelect={this.onAddressChange} onTextChange={this.onTextChange}/>
          <TextField label="Unit/Suite" value={this.state.unit} onChange={(ev, value) => this.setState({ unit: value! })} data-tid="inp_unitSuite" />
        </Stack>
        <DialogFooter>
          <PrimaryButton onClick={this.onEditAddress} disabled={!this.isValid()}>
            Ok
          </PrimaryButton>
          <DefaultButton onClick={this.close}>Cancel</DefaultButton>
        </DialogFooter>
      </Dialog>
    );
  }

  private onAddressChange = (suggest?: SuggestAddress) => {
    const editAddressData: Omit<ProviderAddress, "googlePlaceId"|"ownerEntityId"|"ownerEntityType"|"type"> = {
      addressLine1: suggest?.streetAddress ?? '',
      city: suggest?.city ?? '',
      state: suggest?.state ?? '',
      country: suggest?.country ?? '',
      latitude: suggest?.latitude ?? -1,
      longitude: suggest?.longitude ?? -1,
      zipcode: suggest?.zipcode ?? '',
    };
    this.setState({ suggest, editAddress: { ...this.state.editAddress, ...editAddressData, googlePlaceId: suggest?.googlePlaceId ?? '' } });
    this.setState({ lastSelectedAddress: getFullProviderAddressString(editAddressData)});
  };

  /**
   * Track the live value of the input so we know if it has changed since the last suggestion was selected.
   */
  private onTextChange = (value: string) => {
    this.setState({ currentAddressTextValue: value });
  };

  private onEditAddress = () => {
    const { editAddress, unit } = this.state;

    const addressRequest: Partial<ProviderAddress> = {
      id: editAddress.id,
      addressLine2: unit || '',
      entryInstructions: '',
      type: 'HOME',
      googlePlaceId: editAddress.googlePlaceId,
      ownerEntityType: 'PROVIDER',
      ownerEntityId: this.props.providerId,
    };
    this.props.onEditAddress(addressRequest);
    this.close();
  };

  private isValid(): boolean {
    const unitHasChanged = this.state.unit !== this.state.initialUnitValue;
    const addressHasChanged = this.state.lastSelectedAddress !== this.state.initialAddressValue;
    const addressMatchesLastSelected = this.state.currentAddressTextValue === this.state.lastSelectedAddress;
    return !!this.state.editAddress.googlePlaceId && (unitHasChanged || addressHasChanged) && addressMatchesLastSelected;
  }

  private close = () => {
    this.props.close();
  };
}
